import { z } from 'zod'

export const FederalPovertyLevelSchema = z.object({
  id: z.number().nullable(),
  persons_household: z.number().or(z.string()),
  poverty_guideline: z.string(),
})

export const SetupProgramSchema = z
  .object({
    name: z.string({ message: 'First name is required' }).nullable(),
    enrollment_start_date: z.date().nullable().optional(),
    enrollment_end_date: z.date().nullable().optional(),
    new_application_start_date: z.date().nullable().optional(),
    new_application_end_date: z.date().nullable().optional(),
    program_start_date: z.date().nullable().optional(),
    class_wallet: z.number().nullable().optional(),
    verify_income: z.number().nullable().optional(),
    program_end_date: z.date().nullable().optional(),
    acceptable_income_year: z
      .string({ message: 'Acceptable income year is required' })
      .refine(
        (year) => {
          const yearNumber = Number(year)
          return year.length === 4 && !isNaN(yearNumber)
        },
        {
          message: 'Please enter a valid year in the format YYYY',
        }
      ),
    global_state_id: z
      .number()
      .min(1, { message: 'State is required' })
      .nullable(),
    manager_id: z
      .number()
      .min(1, { message: 'Program Manager is required' })
      .nullable(),
    program_image: z.string().nullable(),
    program_picture_image: z.instanceof(File).optional(),
    created_by: z.string().nullable(),
    unrulr: z.number().nullable().optional(),
    priority_level: z.number().nullable(),
    income_verification: z.string().nullable(),
    acceptable_documents: z.string().nullable(),
    documents_fields: z.string().nullable(),
    federal_poverty_levels: z.array(FederalPovertyLevelSchema).nullable(),
    program_appropiation_amount: z.string().nullable().optional(),
    reward_amount: z.string().nullable().optional(),
    awarded_scholarships_number: z.string().nullable().optional(),
    minimum_age: z.number().nullable().optional(),
    maximum_age: z.number().nullable().optional(),
    cut_off_date: z.date().nullable().optional(),
    residency_state: z.number().nullable().optional(),
    military_exception: z.number().nullable().optional(),
    allow_manual_achievement_verification: z.coerce.number({
      message:
        '[APP_ERR] Allow manual achievement verification must be a number.',
    }),
    achievement_options: z
      .array(z.object({ value: z.string(), label: z.string() }), {
        message: '[APP_ERR] Options must be an array',
      })
      .min(0),
    appeals_window: z.coerce
      .number({
        invalid_type_error: 'Appeals window must be a number',
      })
      .nullable()
      .optional(),
    marketplace_module: z.number().nullable().optional(),
    send_bill_module: z.number().nullable().optional(),
    reimbursement_module: z.number().nullable().optional(),
    require_family_bank_info_for_reimbursement: z
      .number()
      .nullable()
      .optional(),
    pay_provider_module: z.number().nullable().optional(),
    service_fee: z.string().nullable().optional(),
    require_provider_bank_info_for_transactions: z
      .number()
      .nullable()
      .optional(),
    item_sale_tax_enabled: z.number().nullable().optional(),
    service_fee_charged_to: z.number().nullable().optional(),
  })
  .refine(
    (data) => {
      if (data.new_application_start_date && data.new_application_end_date) {
        return data.new_application_end_date > data.new_application_start_date
      }
      return true
    },
    {
      message:
        'New application window open end date must be greater than start date',
      path: ['new_application_end_date'],
    }
  )
  .refine(
    (data) => {
      if (data.program_start_date && data.program_end_date) {
        return data.program_end_date > data.program_start_date
      }
      return true
    },
    {
      message: 'Program school year end date must be greater than start date',
      path: ['program_end_date'],
    }
  )
  .refine(
    (data) => {
      if (data.enrollment_start_date && data.enrollment_end_date) {
        return data.enrollment_end_date > data.enrollment_start_date
      }
      return true
    },
    {
      message:
        'Renewal application window open end date must be greater than start date',
      path: ['enrollment_end_date'],
    }
  )
  .refine(
    (data) => {
      return !(
        data.allow_manual_achievement_verification == 1 &&
        data.achievement_options.length == 0
      )
    },
    {
      message: 'Please select at least one achievement option',
      path: ['achievement_options'],
    }
  )

export type SetupProgramProps = z.infer<typeof SetupProgramSchema>
export type FederalPovertyLevelProps = z.infer<typeof FederalPovertyLevelSchema>

export const ProgramsYears = z.object({
  id: z.number(),
  status: z.number(),
  academic_term_type_id: z.number().nullable(),
  name: z.string(),
  global_state_id: z.number(),
  global_program_id: z.number(),
  period: z.string(),
  program_start_date: z.string(),
  program_end_date: z.string(),
  enrollment_start_date: z.string(),
  enrollment_end_date: z.string(),
  enrolled_scholar_count: z.number().nullable().optional(),
  enrolled_applicants_count: z.number().nullable().optional(),
  enrolled_completed_applicants_count: z.number().nullable().optional(),
  enrolled_rejected_applicants_count: z.number().nullable().optional(),
  global_timezone_code: z.string().nullable().optional(),
})

export type ProgramYearsProps = z.infer<typeof ProgramsYears>

export const ProgramYearsResponse = z.object({
  data: z.array(ProgramsYears),
})

export type ProgramYearsResponseProps = z.infer<typeof ProgramYearsResponse>

export const GlobalProgram = z.object({
  id: z.number(),
  name: z.string(),
  global_state_id: z.number().nullable(),
  status: z.number(),
  domain: z.string(),
  manage: z.number(),
  programs: z.array(ProgramsYears),
})

export type GlobalProgramProps = z.infer<typeof GlobalProgram>

export const GlobalProgramsResponse = z.array(GlobalProgram)

export type GlobalProgramsResponseProps = z.infer<typeof GlobalProgramsResponse>
